/**
 * i18n Configuration — FITD
 *
 * Language detection cascade:
 * 1. localStorage (returning visitor's explicit choice — persists across reloads)
 * 2. navigator.languages (browser native language — first-time visitors)
 * 3. Fallback to 'fr' (primary market: Suisse)
 *
 * When a logged-in user has preferences.language set in Firestore,
 * useLanguageSync() overrides with that value.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';


export const SUPPORTED_LANGUAGES = ['fr', 'en', 'it', 'de-CH'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export interface AppLanguageOption {
  code: AppLanguage;
  label: string;
  flag: string;
  nativeName: string;
  locale: string; // Intl locale for dates/numbers
}

export const APP_LANGUAGES: AppLanguageOption[] = [
  { code: 'fr', label: 'Français', flag: '🇨🇭', nativeName: 'Français', locale: 'fr-CH' },
  { code: 'en', label: 'English', flag: '🇬🇧', nativeName: 'English', locale: 'en-GB' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', nativeName: 'Italiano', locale: 'it-CH' },
  { code: 'de-CH', label: 'Deutsch (CH)', flag: '🇨🇭', nativeName: 'Schweizerdeutsch', locale: 'de-CH' },
];

export const getLanguageOption = (code: string): AppLanguageOption =>
  APP_LANGUAGES.find((l) => l.code === code) || APP_LANGUAGES[0];

i18n.use(HttpBackend).use(LanguageDetector).use(initReactI18next).init({
  supportedLngs: [...SUPPORTED_LANGUAGES],
  // Map German variants (de, de-DE, de-AT) → de-CH, others → fr
  fallbackLng: {
    'de': ['de-CH', 'fr'],
    'de-DE': ['de-CH', 'fr'],
    'de-AT': ['de-CH', 'fr'],
    default: ['fr'],
  },
  defaultNS: 'common',
  ns: ['common', 'onboarding', 'dashboard', 'errors', 'cvBuilder'],

  // Detection: explicit user choice (localStorage) first so it persists across
  // reloads, then browser language for first-time visitors.
  detection: {
    order: ['localStorage', 'navigator'],
    lookupLocalStorage: 'i18nextLng',
    caches: ['localStorage'],
  },

  // Lazy-load translation files from /public/locales/
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  },

  interpolation: {
    escapeValue: false, // React already escapes
  },

  react: {
    useSuspense: true,
  },
});

export default i18n;
